import Button from "../../../common/Button";
import { ButtonVariants } from "../../../common/Button/constants";
import React, { useState } from "react";
import CopyFromBoard from "./CopyFromBoard";
import MarkAllDNP from "./MarkAllDNP";
import { useBoardFromContext } from "../../../../utils/hooks/commonly-stored/uploaded-board/board";
import { Board, boardHasBeenPaidFor } from "../../../../reducers/uploadedBoard";
import Modal from "../../../common/Modal";
import useDownloadBOMFile from "../../../../utils/hooks/commonly-stored/uploaded-board/bomFile";
import ModalContentWithCloseButton from "../../../common/ModalContentWithCloseButton";
import UploadBOMFiles from "../UploadBOMFiles";
import {
  useBillOfMaterials,
  useClearBOM,
} from "../../../../utils/hooks/commonly-stored/uploaded-board/updates/billOfMaterials";
import BOMHistory from "./BOMHistory";
import { DEMO_BOARD_SLUG } from "../../../../constants/boards";
import LoadingOverlay from "../../../LoadingOverlay";
import { ADMIN_GROUP, ENGINEER_GROUP } from "../../../../utils/group";
import { useAppSelector } from "../../../../utils/hooks/store";
import DropdownMenu, { MenuItem } from "../../../common/DropdownMenu";
import {
  useRunPlacementChecks,
  useRunSelectionChecks,
} from "../../../../utils/hooks/commonly-stored/uploaded-board/bomChecks";

const PLACEMENT_CHECK_IN_ADDITIONAL_TOOLS_IS_DISABLED = true;

export default function AdditionalTools({ disabled }: { disabled: boolean }) {
  const board = useBoardFromContext().board as Board;
  const isDemoBoard = board.id == DEMO_BOARD_SLUG;
  const boardPaidFor = boardHasBeenPaidFor(board);
  const billOfMaterials = useBillOfMaterials();
  const { checkPlacement, startingPlacementCheck } = useRunPlacementChecks();
  const { checkSelections, startingSelectionsCheck } = useRunSelectionChecks();
  const { groups } = useAppSelector((state) => state.auth);

  const hasPopulatedElements = billOfMaterials?.elements.some(
    (element) => !element.do_not_populate,
  );
  const downloadBOM = useDownloadBOMFile();
  const clearBOM = useClearBOM();
  const isAdminOrEngineer =
    groups.includes(ADMIN_GROUP) || groups.includes(ENGINEER_GROUP);

  const [showCopyBOM, setShowCopyBOM] = useState(false);
  const [showMarkAllDNP, setShowMarkAllDNP] = useState(false);
  const [showUploadAdditionalFiles, setShowUploadAdditionalFiles] =
    useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const closeModal = () => {
    setShowCopyBOM(false);
    setShowMarkAllDNP(false);
    setShowUploadAdditionalFiles(false);
    setShowHistory(false);
  };

  return (
    <>
      <LoadingOverlay
        loading={startingPlacementCheck}
        text={"Starting placement checks..."}
      />
      <LoadingOverlay
        loading={startingSelectionsCheck}
        text={"Starting selection checks..."}
      />
      <DropdownMenu
        placement={"bottom-end"}
        dropdownTrigger={
          <Button
            label={"Additional tools"}
            rightIconProps={{
              src: "/icons/expand_gray.svg",
              alt: "Copy",
              iconDimension: 10,
            }}
            variant={ButtonVariants.SECONDARY}
            disabled={disabled}
          />
        }
        disabled={disabled}
      >
        {!PLACEMENT_CHECK_IN_ADDITIONAL_TOOLS_IS_DISABLED && (
          <MenuItem onClick={checkPlacement}>Check part placement</MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (!boardPaidFor) {
              clearBOM();
            }
          }}
          disabled={boardPaidFor}
        >
          Remove all elements
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!boardPaidFor || isAdminOrEngineer) {
              setShowCopyBOM(true);
            }
          }}
          disabled={boardPaidFor && !isAdminOrEngineer}
        >
          Copy bill of materials from board
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!boardPaidFor || isAdminOrEngineer) {
              setShowUploadAdditionalFiles(true);
            }
          }}
          disabled={
            !hasPopulatedElements || (boardPaidFor && !isAdminOrEngineer)
          }
        >
          Upload additional BOM or pick and place files
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!boardPaidFor && hasPopulatedElements) {
              setShowMarkAllDNP(true);
            }
          }}
          disabled={!hasPopulatedElements || boardPaidFor}
        >
          Mark all do not populate
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (hasPopulatedElements) {
              void downloadBOM(board);
            }
          }}
          disabled={!hasPopulatedElements || isDemoBoard}
        >
          Download bill of materials as Excel file
        </MenuItem>
        <MenuItem onClick={() => setShowHistory(true)} disabled={isDemoBoard}>
          See bill of materials history
        </MenuItem>
        {isAdminOrEngineer && (
          <MenuItem onClick={checkSelections} disabled={isDemoBoard}>
            Run part selection checks
          </MenuItem>
        )}
      </DropdownMenu>
      <Modal isOpen={showCopyBOM} onRequestClose={closeModal}>
        <CopyFromBoard close={closeModal} />
      </Modal>
      <MarkAllDNP show={showMarkAllDNP} close={closeModal} />
      <Modal isOpen={showUploadAdditionalFiles} onRequestClose={closeModal}>
        <ModalContentWithCloseButton closeModal={closeModal}>
          <UploadBOMFiles onUpload={closeModal} />
        </ModalContentWithCloseButton>
      </Modal>
      <Modal isOpen={showHistory} onRequestClose={closeModal}>
        <ModalContentWithCloseButton
          title={"Bill of Materials History"}
          closeModal={closeModal}
        >
          <BOMHistory />
        </ModalContentWithCloseButton>
      </Modal>
    </>
  );
}
